import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useSports } from 'containers/Mundial2026/context/SportsProvider';
import GroupTable from 'containers/Mundial2026/WCStandings/components/GroupTable/GroupTable';
import { getTranslationEmptyOrNullKey } from 'utils/getTranslation';
import styles from './Classification.module.scss';

const ACOTACIONES = {
  PTS: 'eventodeportivo_siglas_texto_puntos',
  PTD: 'eventodeportivo_siglas_texto_partidosdisputados',
  G: 'eventodeportivo_siglas_texto_goles',
  E: 'eventodeportivo_siglas_texto_empatados',
  P: 'eventodeportivo_siglas_texto_perdidos',
  GF: 'eventodeportivo_siglas_texto_golesafavor',
  GC: 'eventodeportivo_siglas_texto_golesencontra',
  GD: 'eventodeportivo_siglas_texto_diferenciadegoles',
};

const FIXED_COLUMNS = [
  { key: 'rank', label: 'RK' },
  { key: 'team.name', label: 'País' },
];

const STATS_COLUMNS = [
  { key: 'stats.PTS', label: 'PTS' },
  { key: 'stats.PTD', label: 'PTD' },
  { key: 'stats.G', label: 'G' },
  { key: 'stats.E', label: 'E' },
  { key: 'stats.P', label: 'P' },
  { key: 'stats.GF', label: 'GF' },
  { key: 'stats.GC', label: 'GC' },
  { key: 'stats.GD', label: 'GD' },
];

const ACOTACIONES_COLUMNS = ['PTS', 'P', 'PTD', 'GF', 'G', 'GC', 'E', 'GD'];

const TEAM_CODE_POR_DEFINIR = 'PD';

// Aplana stats: { PTS: { value: 7, description } } → { PTS: 7 }.
const mapStats = (stats = {}) => ({
  PTS: stats.PTS?.value ?? 0,
  PTD: stats.PTD?.value ?? 0,
  G: stats.G?.value ?? 0,
  E: stats.E?.value ?? 0,
  P: stats.P?.value ?? 0,
  GF: stats.GF?.value ?? 0,
  GC: stats.GC?.value ?? 0,
  GD: stats.GD?.value ?? 0,
});

const resolveCode = (team, teamCodeByTeamId) => {
  if (!team.flagUrl) return TEAM_CODE_POR_DEFINIR;
  return teamCodeByTeamId[team.teamId] ?? TEAM_CODE_POR_DEFINIR;
};

const mapClassificationToRows = (standings = [], teamCodeByTeamId = {}) =>
  standings.map(item => ({
    rank: item.position,
    name: item.team.name,
    code: resolveCode(item.team, teamCodeByTeamId),
    id: item.team.teamId,
    flag: item.team.flagUrl,
    isPlaceholder: item.team.isPlaceholder ?? false,
    stats: mapStats(item.stats),
  }));

const mapRankingsToRows = (teams = [], teamCodeByTeamId = {}) =>
  teams.map(item => ({
    rank: item.position,
    name: item.team.name,
    code: resolveCode(item.team, teamCodeByTeamId),
    id: item.team.teamId,
    flag: item.team.flagUrl,
    isPlaceholder: false,
    stats: mapStats(item.stats),
  }));

const EmptyState = () => (
  <p className={styles['classification__empty']}>
    Sin datos de clasificación disponibles.
  </p>
);

// Footer de acotaciones — reutilizado por las dos ramas (grupo / ranking).
// Mismo patrón que Ranking.jsx: itera STATS_COLUMNS y resuelve cada label
// vía ACOTACIONES + getTranslationEmptyOrNullKey.
const Acotaciones = ({ translations }) => (
  <div className={styles['classification__acotaciones']}>
    <div className={styles['classification__acotaciones-grid']}>
      {ACOTACIONES_COLUMNS.map(label => (
        <p key={label} className={styles['classification__acotacion']}>
          <strong className={styles['classification__acotacion-abbr']}>
            {label}:
          </strong>{' '}
          {getTranslationEmptyOrNullKey(ACOTACIONES?.[label], translations)}
        </p>
      ))}
    </div>
  </div>
);

const Classification = () => {
  const { countries, currentStage } = useSports();
  const translations = useSelector(state => state.translations);
  const wcCountries = useSelector(state => state.wcCountries?.countries || []);
  const { data } = countries || {};

  const teamCodeByTeamId = useMemo(
    () =>
      wcCountries.reduce((acc, team) => {
        if (team.id) acc[team.id] = team.code;
        return acc;
      }, {}),
    [wcCountries],
  );

  const statsColumnsWithTooltips = useMemo(
    () =>
      STATS_COLUMNS.map(col => {
        const cmsKey = ACOTACIONES[col.label];
        const raw = cmsKey
          ? getTranslationEmptyOrNullKey(cmsKey, translations)
          : '';
        const tooltip = !raw || raw === cmsKey ? col.label : raw;
        return { ...col, tooltip };
      }),
    [translations],
  );

  // Equipo del detalle actual: GroupTable lo usa para resaltar la fila
  // cuyo row.name coincida, con un bg derivado de su primaryHex (alpha 0.2).
  const highlightTeamName = data?.teamCard?.team?.name;
  const highlightColor = data?.teamCard?.team?.primaryHex;

  const isRankingPhase =
    currentStage?.stage?.code !== 'PHASE_GROUP' &&
    currentStage?.stage?.code !== 'GROUP_PHASE';

  if (!isRankingPhase) {
    const groupData = data?.classification?.[0];
    if (!groupData) {
      return <div className={styles['classification']} />;
    }

    return (
      <div className={styles['classification']}>
        <GroupTable
          groupName={groupData.group}
          rows={mapClassificationToRows(groupData.standings, teamCodeByTeamId)}
          fixedColumns={FIXED_COLUMNS}
          statsColumns={statsColumnsWithTooltips}
          highlightTeamName={highlightTeamName}
          highlightColor={highlightColor}
        />
        <Acotaciones translations={translations} />
      </div>
    );
  }

  const rankingData = data?.rankings?.[0];
  if (!rankingData) {
    return (
      <div className={styles['classification']}>
        <EmptyState />
      </div>
    );
  }

  return (
    <div className={styles['classification']}>
      <GroupTable
        groupName="Ranking"
        rows={mapRankingsToRows(rankingData.teams, teamCodeByTeamId)}
        fixedColumns={FIXED_COLUMNS}
        statsColumns={statsColumnsWithTooltips}
        highlightTeamName={highlightTeamName}
        highlightColor={highlightColor}
      />
      <Acotaciones translations={translations} />
    </div>
  );
};

export default Classification;
